import { useContext } from "react";

import {
  lastSignedInSession,
  SessionContext,
  setLastSignedInSession,
  type Session,
  type SessionContextValue,
  type SessionUser,
} from "@/context/session-context";

export type { Session, SessionUser };

export function useSessionState(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSessionState must be used inside a SessionProvider");
  return value;
}

/**
 * The signed-in session, for code that only runs behind a guard.
 *
 * Throws rather than returning null, so a page never has to write
 * `session?.user?.name` for a value the route guard has already guaranteed. If
 * this throws, the bug is a missing guard on the route, and the error says so.
 *
 * During sign-out, context consumers re-render before they unmount. Returning the
 * last session for that one frame lets `RequireSession` finish navigating to
 * `/login` instead of crashing the tree.
 */
export function useSession(): Session {
  const state = useSessionState();
  if (state.status === "signedIn") {
    setLastSignedInSession(state.session);
    return state.session;
  }
  if (lastSignedInSession) return lastSignedInSession;
  throw new Error("useSession called outside an authenticated route");
}
